import React from 'react';
import { useChatStore } from '../../store/chatStore';
import UserAvatar from '../ui/UserAvatar';
import { EMPTY_OBJECT } from './chatMessageUtils';

// Skeleton layout while a conversation's first page loads: fake message rows
// shaped like the real ones (avatar + name + text lines, some author-grouped)
// so the content pop-in doesn't reflow the frame. Widths are a fixed pattern,
// not random — random would re-roll on every render and shimmer unevenly.
// header: show avatar+name row; lines: text bar widths in %.
export const SKELETON_ROWS = [
  { header: true, lines: [72] },
  { header: false, lines: [46] },
  { header: true, lines: [85, 58] },
  { header: true, lines: [31] },
  { header: false, lines: [64] },
  { header: false, lines: [52, 27] },
  { header: true, lines: [68] },
  { header: true, lines: [42, 76] },
];

export function MessagesSkeleton({ rows }) {
  const pattern = rows ? SKELETON_ROWS.slice(0, rows) : SKELETON_ROWS;
  return (
    <div className="chat-skel" aria-hidden="true">
      {pattern.map((row, i) => (
        <div key={i} className="chat-skel-row">
          {row.header ? (
            <span className="chat-skel-shimmer chat-skel-avatar" />
          ) : (
            <span className="chat-skel-gutter" />
          )}
          <div className="chat-skel-lines">
            {row.header && <span className="chat-skel-shimmer chat-skel-bar chat-skel-bar--name" />}
            {row.lines.map((w, j) => (
              <span key={j} className="chat-skel-shimmer chat-skel-bar" style={{ width: `${w}%` }} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * Typing indicator strip. Its own component (and store subscription) so the
 * every-few-seconds typing pings re-render just this one line, not the whole
 * message list above it.
 */
export function TypingStrip({ channelId }) {
  const typing = useChatStore((s) => s.typingByChannel[channelId]) || EMPTY_OBJECT;
  const entries = Object.entries(typing).filter(([, t]) => t?.name);
  const shown = entries.slice(0, 3);
  const overflow = entries.length > 3;

  return (
    <div className={`chat-typing${entries.length ? ' chat-typing--active' : ''}`}>
      {entries.length > 0 && (
        <>
          <span className="chat-typing-avatars" aria-hidden>
            {shown.map(([uid, t]) => (
              <UserAvatar
                key={uid}
                className="chat-avatar chat-typing-avatar"
                userId={Number(uid)}
                name={t.name}
                size={18}
              />
            ))}
          </span>
          <span className="chat-typing-text">
            {shown.map(([uid, t], i) => (
              <React.Fragment key={uid}>
                <strong>{t.name}</strong>
                {i < shown.length - 1 && (i === shown.length - 2 && !overflow ? ' and ' : ', ')}
              </React.Fragment>
            ))}
            {overflow ? ' and others are typing' : shown.length === 1 ? ' is typing' : ' are typing'}
          </span>
          {/* The animated dots ARE the ellipsis — no static "…" doubling it. */}
          <span className="chat-typing-dots" aria-hidden>
            <i />
            <i />
            <i />
          </span>
        </>
      )}
    </div>
  );
}
